import { useRef, useState } from 'react'
import { useGetLegalDocumentsQuery, useDeleteLegalDocumentMutation } from '../../store/api/legalApi'
import type { LegalDocument } from '../../store/api/legalApi'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { api, getErrorMessage } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { Button } from '../../components/ui'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

const SLOTS: { type: LegalDocument['type']; label: string; description: string; icon: string }[] = [
  { type: 'user',      label: 'User Legal Document',      description: 'Terms/agreement shown to regular platform users.', icon: '📄' },
  { type: 'developer', label: 'Developer Legal Document', description: 'Terms/agreement for external developers registering agents.', icon: '🧾' },
]

const fmtSize = (bytes: number) => bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

function DocumentSlot({ slot, doc, onChanged }: { slot: typeof SLOTS[number]; doc: LegalDocument | undefined; onChanged: () => void }) {
  const { accessToken } = useAppSelector((st) => st.auth)
  const [deleteDocument] = useDeleteLegalDocumentMutation()
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const hasFile = !!doc?.filename

  const handleDownload = async () => {
    setDownloading(true); setError(null)
    try {
      const { blob, filename } = await api.downloadFile('payments', ENDPOINTS.legal.download(slot.type), accessToken)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ?? doc?.filename ?? `${slot.type}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(getErrorMessage(err, 'Download failed'))
    } finally {
      setDownloading(false)
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') { setError('Only PDF files are allowed'); return }
    if (file.size > MAX_FILE_SIZE_BYTES) { setError('File size exceeds 10MB'); return }
    setUploading(true); setError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await api.upload('payments', ENDPOINTS.legal.adminUpload(slot.type), formData, accessToken)
      onChanged()
    } catch (err) {
      setError(getErrorMessage(err, 'Upload failed'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Remove the ${slot.label}? This can't be undone.`)) return
    try {
      await deleteDocument(slot.type).unwrap()
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to remove document'))
    }
  }

  return (
    <div style={{ padding: '1rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
        <div style={{ fontSize: '1.3rem' }}>{slot.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{slot.label}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{slot.description}</div>
        </div>
      </div>

      {hasFile ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.75rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc!.filename}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
              {fmtSize(doc!.fileSizeBytes!)} · uploaded {fmtDate(doc!.updatedAt!)}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => void handleDownload()} loading={downloading}>Download</Button>
          <Button size="sm" variant="ghost" onClick={() => void handleDelete()}>Remove</Button>
        </div>
      ) : (
        <div style={{ fontSize: '0.8rem', color: 'var(--warning)' }}>⚠️ No document uploaded yet.</div>
      )}

      <div>
        <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={(e) => void handleUpload(e)} />
        <Button size="sm" onClick={() => fileInputRef.current?.click()} loading={uploading}>
          {hasFile ? 'Replace PDF' : 'Upload PDF'}
        </Button>
      </div>

      {error && <div style={{ fontSize: '0.75rem', color: 'var(--error)' }}>{error}</div>}
    </div>
  )
}

export function AdminDocuments() {
  const { data, isFetching, refetch } = useGetLegalDocumentsQuery()
  const documents = data?.documents ?? []

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>📁 Documents</div>
        <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Upload the legal PDFs for each audience. These are stored securely but aren't linked anywhere in the app yet.
      </p>

      <div className={s.listCard} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', padding: '1rem', background: 'transparent', border: 'none' }}>
        {SLOTS.map((slot) => (
          <DocumentSlot
            key={slot.type}
            slot={slot}
            doc={documents.find((d) => d.type === slot.type)}
            onChanged={() => void refetch()}
          />
        ))}
      </div>
    </div>
  )
}
